import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import { fileUrl } from "../../services/api";

const tile = {
  position: "relative",
  width: 88,
  height: 88,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "2px",
  overflow: "hidden",
  backgroundColor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const removeBtn = {
  position: "absolute",
  top: 2,
  right: 2,
  width: 18,
  height: 18,
  p: 0,
  backgroundColor: "rgba(0,0,0,0.55)",
  color: "#fff",
  borderRadius: "2px",
  "&:hover": { backgroundColor: "rgba(187,0,0,0.85)" },
};

export default function PhotoSection({
  title,
  folder,
  existing = [],
  newFiles = [],
  addFiles,
  removeExisting,
  removeNew,
  readOnly = false,
  uploading = false,
}) {
  const inputRef = useRef(null);

  const previews = useMemo(
    () => newFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [newFiles]
  );

  useEffect(() => {
    return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [previews]);

  const total = existing.length + newFiles.length;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "2px",
        p: 1,
        mt: 1,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ImageIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({total})
          </Typography>
        </Stack>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            sx={{ fontSize: "0.6875rem", py: 0.25, minHeight: 0 }}
          >
            {uploading ? "Uploading…" : "Add Photos"}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) addFiles?.(Array.from(e.target.files));
            e.target.value = "";
          }}
        />
      </Stack>

      {total === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No photos yet.
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {existing.map((name) => (
            <Box key={name} sx={tile}>
              <a href={fileUrl(folder, name)} target="_blank" rel="noreferrer" style={{ display: "block", width: "100%", height: "100%" }}>
                <img
                  src={fileUrl(folder, name)}
                  alt={name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </a>
              {!readOnly && (
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeExisting?.(name)} sx={removeBtn}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          {previews.map((p, i) => (
            <Box key={p.url} sx={{ ...tile, borderStyle: "dashed", borderColor: "primary.main" }}>
              <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }} />
              {!readOnly && (
                <Tooltip title="Remove (not yet uploaded)">
                  <IconButton size="small" onClick={() => removeNew?.(i)} sx={removeBtn}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
